import { Component, OnInit, Input, OnChanges, SimpleChanges, OnDestroy } from '@angular/core';
import { MdDialog } from '@angular/material';
import {
  PaymentInformation,
  BookingCancellationCharge
} from 'app/model/approval/approval-detail-model';
import { BookerPaymentInformation, FleetInformation } from 'app/model/booking/my-booking-model-v2';
import { PaymentMethodDetailType, PaymentType } from 'app/model/constant';
import { AppConfigService } from 'app/services/app-config/app-config.service';
import { Subscription } from 'rxjs';
import { DialogTaxesFeesComponent } from '../cart/dialog-taxes-fees/dialog-taxes-fees.component';
import { UtilitiesService } from 'app/services/utilities/utilities.service';

class Condition {
  public static readonly BOOKING_SINGLE_PAYMENT = 'BOOKING_SINGLE_PAYMENT';
  public static readonly CANCEL_SINGLE_PAYMENT = 'CANCEL_SINGLE_PAYMENT';
  public static readonly CANCEL_SINGLE_PAYMENT_NON_CREDIT_TERM = 'CANCEL_SINGLE_PAYMENT_NON_CREDIT_TERM';
  public static readonly BOOKING_MULTI_PAYMENT = 'BOOKING_MULTI_PAYMENT';
  public static readonly CANCEL_MULTI_PAYMENT = 'CANCEL_MULTI_PAYMENT';
}

@Component({
  selector: 'act-payment-summary',
  templateUrl: './payment-summary.component.html',
  styleUrls: ['./payment-summary.component.scss']
})
export class PaymentSummaryComponent implements OnInit, OnChanges, OnDestroy {
  @Input() bookingPayment: PaymentInformation;
  @Input() bookingCancellationCharge: BookingCancellationCharge;
  @Input() mainPayment: BookerPaymentInformation;
  @Input() anotherPayment: BookerPaymentInformation;
  @Input() mainPaymentMethod: string;
  @Input() anotherPaymentMethod: string;
  @Input() coBookerMainPayment: BookerPaymentInformation;

  @Input() isCancelBooking = false;
  @Input() surchargeList: string[] = [];
  @Input() mini = false;
  @Input() prefixElementId: string;
  @Input() isRejectBooking: boolean;
  @Input() paymentType: string;

  public Condition = Condition;
  public PaymentMethodDetailType = PaymentMethodDetailType;
  public condition = Condition.BOOKING_MULTI_PAYMENT;
  public isShareBudget = false;
  public isMultiPayment = false;
  public lang: string;
  public bookerPayments: BookerPaymentInformation[];
  public isShareBudgetByAdmin = false;
  public mainPaymentMethodDisplay: string;
  public anotherPaymentMethodDisplay: string;
  public hasDiscount = false;
  public totalPriceBeforeDiscount = 0;
  public discountAmount = 0;

  private langSubscription: Subscription;

  constructor(
    private appConfigService: AppConfigService,
    private dialog: MdDialog,
    private utilitiesService: UtilitiesService,
  ) { }

  ngOnInit() {
    this.lang = this.appConfigService.getLanguage();
    this.langSubscription && this.langSubscription.unsubscribe();
    this.langSubscription = this.appConfigService.getLanguageObs()
      .subscribe(lang => this.lang = lang);
    this.prepareMainPaymentMethodDisplay(this.mainPaymentMethod);
    this.prepareAnotherPaymentMethodDisplay(this.anotherPaymentMethod);
    this.prepareDiscountSummary();
  }

  ngOnChanges(changes: SimpleChanges) {
    for (const propName in changes) {
      if (changes.hasOwnProperty(propName)) {
        switch (propName) {
          case 'mainPayment':
          case 'anotherPayment':
          case 'coBookerMainPayment':
          case 'bookingPayment':
            this.calculateCondition();
            this.prepareDiscountSummary();
            break;
        }
      }
    }
  }

  ngOnDestroy() {
    this.langSubscription && this.langSubscription.unsubscribe();
  }

  calculateCondition() {
    if (this.mainPayment && this.coBookerMainPayment) {
      this.bookerPayments = [this.mainPayment, this.coBookerMainPayment];
      this.isShareBudget = true;
    }
    if (this.anotherPayment) {
      this.isMultiPayment = true;
    }

    if (!this.isCancelBooking && !this.isMultiPayment) {
      this.condition = Condition.BOOKING_SINGLE_PAYMENT;

    } else if (this.isCancelBooking && !this.isMultiPayment) {
      this.condition = this.mainPaymentMethod !== PaymentMethodDetailType.CREDIT_TERMS ?
        Condition.CANCEL_SINGLE_PAYMENT_NON_CREDIT_TERM : Condition.CANCEL_SINGLE_PAYMENT;

    } else if (!this.isCancelBooking && this.isMultiPayment) {
      this.condition = Condition.BOOKING_MULTI_PAYMENT;

    } else if (this.isCancelBooking && this.isMultiPayment) {
      this.condition = Condition.CANCEL_MULTI_PAYMENT;
    }
    this.isShareBudgetByAdmin = this.shareBudgetByAdmin();
  }

  shareBudgetByAdmin() {
    return this.paymentType === PaymentType.SHARE_BUDGET_BY_ADMIN || this.paymentType === PaymentType.SHARE_BUDGET_BY_ADMIN_MULTI_PAYMENT;
  }

  prepareMainPaymentMethodDisplay(paymentMethod: string) {
    let paymentMethodDisplay;
    switch (paymentMethod) {
      case PaymentMethodDetailType.CREDIT_TERMS:
        paymentMethodDisplay = 'payment-summary-pay-credit-term';
        break;
      case PaymentMethodDetailType.CORPORATE_FLEET:
        paymentMethodDisplay = 'payment-summary-pay-corporate-fleet';
        break;
      case PaymentMethodDetailType.PERSONAL_FLEET:
        paymentMethodDisplay = 'payment-summary-pay-personal-fleet';
        break;
    }
    this.mainPaymentMethodDisplay = paymentMethodDisplay;
  }

  prepareAnotherPaymentMethodDisplay(paymentMethod: string) {
    let paymentMethodDisplay;
    switch (paymentMethod) {
      case PaymentMethodDetailType.CREDIT_CARD:
        paymentMethodDisplay = 'payment-summary-pay-credit-card-add';
        break;
      case PaymentMethodDetailType.TRUE_MONEY:
        paymentMethodDisplay = 'payment-summary-pay-true-money-add';
        break;
      case PaymentMethodDetailType.PERSONAL_FLEET:
        paymentMethodDisplay = 'payment-summary-pay-personal-fleet-add';
        break;
    }
    this.anotherPaymentMethodDisplay = paymentMethodDisplay;
  }

  openDialogTaxesAndFees() {
    if (this.utilitiesService.isMobile()) {
      const dialogRef = this.dialog.open(DialogTaxesFeesComponent);
    }
  }

  prepareDiscountSummary() {
    this.hasDiscount = !!(this.bookingPayment && this.bookingPayment.discount > 0);
    this.discountAmount = this.hasDiscount ? this.bookingPayment.discount : 0;
    this.totalPriceBeforeDiscount = this.hasDiscount ? (this.bookingPayment.totalPrice + this.discountAmount) : 0;
  }
}



// WEBPACK FOOTER //
// ./src/app/components/payment-summary/payment-summary.component.ts